import React, { useContext, useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { PrayerPartnerContext } from "../contexts/PrayerPartnerContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageLoader } from "../components/page-loader";
import { NotFoundPage } from "./not-found-page";
import { TestimonyDialog } from "../components/dialogs/TestimonyDialog";
import PastoralPagination from "../components/pastoral-care/components/pagination";

/** Shared messages contain only the testimony text. */
function testimonyShareBody(row) {
  return String(row?.testimony || "").trim();
}

/** Slack incoming webhooks reject some payloads; keep plain text and under size limits. */
const SLACK_TEXT_MAX_CHARS = 35000;
/** mailto links fail once the encoded URL gets too long. */
const MAILTO_URL_MAX = 1800;

function slackSafePlainText(s) {
  if (s == null) return "";
  return String(s)
    .replace(/\u0000/g, "")
    .replace(/[\u0001-\u0008\u000b\u000c\u000e-\u001f]/g, "")
    .slice(0, SLACK_TEXT_MAX_CHARS);
}

function joinTestimonyTexts(rows) {
  return rows
    .map((row) => testimonyShareBody(row))
    .filter(Boolean);
}

function chunkTestimonyTexts(texts) {
  const chunks = [];
  let bucket = [];
  let size = 0;
  texts.forEach((text) => {
    const safe = slackSafePlainText(text);
    const addition = (bucket.length ? 5 : 0) + safe.length;
    if (bucket.length && size + addition > SLACK_TEXT_MAX_CHARS) {
      chunks.push(bucket);
      bucket = [safe];
      size = safe.length;
      return;
    }
    bucket.push(safe);
    size += addition;
  });
  if (bucket.length) chunks.push(bucket);
  return chunks;
}

async function openTestimonyEmail(subject, body) {
  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  if (mailto.length <= MAILTO_URL_MAX) {
    window.open(mailto, "_blank", "noopener,noreferrer");
    return;
  }
  try {
    await navigator.clipboard.writeText(body);
    toast.info(
      "Testimony text copied — paste it into the email. The message was too long to attach automatically."
    );
  } catch {
    toast.error("Could not open email. The message is too long to attach automatically.");
    return;
  }
  window.open(
    `mailto:?subject=${encodeURIComponent(subject)}`,
    "_blank",
    "noopener,noreferrer"
  );
}

function shareTestimonyByEmail(row) {
  const body = testimonyShareBody(row);
  if (!body) {
    toast.error("This testimony has no text to send");
    return;
  }
  openTestimonyEmail("Testimony", body);
}

export const TestimonyPage = () => {
  const {
    fetchTestimony,
    fetchReviewedTestimonies,
    updateTestimonyStatus,
    removeTestimonyStatus,
    postTestimonySlack,
  } = useContext(PrayerPartnerContext);
  const [activeTab, setActiveTab] = useState("new");
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewedPage, setReviewedPage] = useState(1);
  const [itemsPerPage] = useState(10); // Set your desired items per page
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCallDetail, setSelectedCallDetail] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isSendingBatch, setIsSendingBatch] = useState(false);

  const getTestimony = () => {
    window.scrollTo({ top: 0 });
    return fetchTestimony(currentPage);
  };

  const { isPending, isError, data } = useQuery({
    queryKey: ["testimonies", currentPage],
    queryFn: getTestimony,
    enabled: activeTab === "new",
  });

  const {
    isPending: isReviewedPending,
    isError: isReviewedError,
    data: reviewedPayload,
  } = useQuery({
    queryKey: ["testimonies-reviewed"],
    queryFn: fetchReviewedTestimonies,
    enabled: activeTab === "reviewed",
  });

  const handleReviewTestimony = async (callDetail) => {
    setSelectedCallDetail(callDetail);
    setIsDialogOpen(true);
  };

  const handleRemoveTestimony = async (testimonyId) => {
    try {
      await removeTestimonyStatus(testimonyId);
      queryClient.invalidateQueries({ queryKey: ["testimonies"] });
      queryClient.invalidateQueries({ queryKey: ["testimonies-reviewed"] });
    } catch (error) {
      console.error("Error removing testimony status:", error);
    }
  };

  const handleDialogSuccess = () => {
    // After reviewing testimony, update the call detail status
    if (selectedCallDetail) {
      updateTestimonyStatus(selectedCallDetail.id);
      queryClient.invalidateQueries({ queryKey: ["testimonies"] });
      queryClient.invalidateQueries({ queryKey: ["testimonies-reviewed"] });
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedCallDetail(null);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const reviewedListFull = useMemo(() => {
    if (!reviewedPayload) return [];
    if (Array.isArray(reviewedPayload.data)) return reviewedPayload.data;
    if (Array.isArray(reviewedPayload)) return reviewedPayload;
    return [];
  }, [reviewedPayload]);

  const categoryOptions = useMemo(() => {
    const categories = new Set();
    reviewedListFull.forEach((row) => {
      if (row.category) categories.add(row.category);
    });
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [reviewedListFull]);

  const hasUncategorized = useMemo(
    () => reviewedListFull.some((row) => !row.category),
    [reviewedListFull]
  );

  const filteredReviewed = useMemo(() => {
    if (categoryFilter === "all") return reviewedListFull;
    if (categoryFilter === "uncategorized") {
      return reviewedListFull.filter((row) => !row.category);
    }
    return reviewedListFull.filter((row) => row.category === categoryFilter);
  }, [reviewedListFull, categoryFilter]);

  const reviewedTotal = filteredReviewed.length;
  const reviewedTotalPages = Math.max(1, Math.ceil(reviewedTotal / itemsPerPage));
  const safeReviewedPage = Math.min(
    Math.max(1, reviewedPage),
    reviewedTotalPages
  );
  const reviewedRows = useMemo(() => {
    const start = (safeReviewedPage - 1) * itemsPerPage;
    return filteredReviewed.slice(start, start + itemsPerPage);
  }, [filteredReviewed, safeReviewedPage, itemsPerPage]);

  const filteredIds = useMemo(
    () => filteredReviewed.map((row) => row.id),
    [filteredReviewed]
  );
  const selectedRows = useMemo(
    () => filteredReviewed.filter((row) => selectedIds.has(row.id)),
    [filteredReviewed, selectedIds]
  );
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const handleReviewedPageChange = (pageNumber) => {
    setReviewedPage(pageNumber);
    window.scrollTo({ top: 0 });
  };

  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
    setReviewedPage(1);
  };

  const toggleRowSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const sendTestimoniesToSlack = async (rows) => {
    const texts = joinTestimonyTexts(rows);
    if (!texts.length) {
      toast.error("Selected testimonies have no text to send");
      return;
    }
    const chunks = chunkTestimonyTexts(texts);
    try {
      for (let i = 0; i < chunks.length; i += 1) {
        const joined = chunks[i].join("\n\n---\n\n");
        const prefix = chunks.length > 1 ? `(${i + 1}/${chunks.length})\n\n` : "";
        const payloadText = slackSafePlainText(`${prefix}${joined}`) || "Testimony";
        const result = await postTestimonySlack({
          text: payloadText,
          ...(rows.length === 1 && chunks.length === 1
            ? { testimony_id: rows[0].id }
            : {}),
        });
        if (!result.ok) {
          console.error("Slack proxy error:", result.message);
          toast.error(result.message || "Could not send to Slack");
          try {
            await navigator.clipboard.writeText(texts.join("\n\n---\n\n"));
            toast.info(
              "Copied to clipboard — paste in Slack or add POST /testimony/slack on the API"
            );
          } catch {
            /* ignore */
          }
          return;
        }
      }
      toast.success(
        texts.length > 1
          ? `Sent ${texts.length} testimonies to Slack`
          : "Sent to Slack"
      );
    } catch (e) {
      console.error(e);
      toast.error("Could not send to Slack. Try again or use Email.");
    }
  };

  const handleShareTestimonySlack = (row) => sendTestimoniesToSlack([row]);

  const handleBatchSlack = async () => {
    if (!selectedRows.length || isSendingBatch) return;
    setIsSendingBatch(true);
    try {
      await sendTestimoniesToSlack(selectedRows);
    } finally {
      setIsSendingBatch(false);
    }
  };

  const handleBatchEmail = () => {
    const texts = joinTestimonyTexts(selectedRows);
    if (!texts.length) {
      toast.info("Select at least one testimony with text");
      return;
    }
    const subject = texts.length === 1 ? "Testimony" : `Testimonies (${texts.length})`;
    openTestimonyEmail(subject, texts.join("\n\n---\n\n"));
  };

  const testimonies = activeTab === "new" && data ? data.data : [];
  const totalCount = activeTab === "new" && data ? data.total_count : 0;

  if (activeTab === "new") {
    if (isPending) return <PageLoader />;
    if (isError) return <NotFoundPage />;
  } else {
    if (isReviewedPending) return <PageLoader />;
    if (isReviewedError) return <NotFoundPage />;
  }

  return (
    <div className="m-3">
      <header className="mb-3 text-center font-['Futura_PT'] bg-[#F1F1F1] rounded p-2 text-[#178B96]">
        <h1>Testimonials</h1>
      </header>

      <div className="flex justify-center gap-2 mb-4 font-['Futura_PT']">
        <button
          type="button"
          onClick={() => setActiveTab("new")}
          className={`px-5 py-2 rounded text-sm font-semibold tracking-wide transition-colors border border-[#00323b] ${
            activeTab === "new"
              ? "bg-[#178B96] text-white"
              : "bg-white text-[#178B96] hover:bg-[#f1f1f1]"
          }`}
        >
          New
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("reviewed")}
          className={`px-5 py-2 rounded text-sm font-semibold tracking-wide transition-colors border border-[#00323b] ${
            activeTab === "reviewed"
              ? "bg-[#178B96] text-white"
              : "bg-white text-[#178B96] hover:bg-[#f1f1f1]"
          }`}
        >
          Reviewed
        </button>
      </div>

      {activeTab === "new" && testimonies && (
        <div className="p-2 m-2 px-4">
          <TableTestimony className="px-3">
            <thead>
              <tr>
                <th>Caller Name</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {testimonies.map((testimony, index) => (
                <tr key={index}>
                  <td className="text-center">
                    {testimony.caller_first_name} {testimony.caller_last_name}
                  </td>

                  <td className="text-center">
                    <div className="flex justify-center gap-2">
                      {testimony.is_testimony === 1 ? (
                        <>
                          <button
                            onClick={() => handleReviewTestimony(testimony)}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors"
                          >
                            Reviewed
                          </button>

                          <button
                            onClick={() => handleRemoveTestimony(testimony.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                          >
                            Remove Testimony
                          </button>
                        </>
                      ) : testimony.is_testimony === 2 ? (
                        <span className="bg-gray-500 text-white px-3 py-1 rounded text-sm">
                          Reviewed
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableTestimony>
        </div>
      )}

      {activeTab === "reviewed" && (
        <div className="p-2 m-2 px-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3 font-['Futura_PT']">
            <label className="flex items-center gap-2 text-sm text-[#178B96]">
              <span className="font-semibold tracking-wide">Category</span>
              <select
                value={categoryFilter}
                onChange={(e) => handleCategoryFilterChange(e.target.value)}
                className="border border-[#00323b] rounded px-2 py-1 text-sm text-[#178B96] bg-white"
              >
                <option value="all">All categories</option>
                {hasUncategorized && (
                  <option value="uncategorized">Uncategorized</option>
                )}
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[#178B96]">
                {selectedRows.length} selected
              </span>
              <button
                type="button"
                disabled={!selectedRows.length || isSendingBatch}
                onClick={handleBatchSlack}
                className="bg-[#4A154B] hover:bg-[#611f69] disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded text-xs transition-colors"
              >
                Send to Slack
              </button>
              <button
                type="button"
                disabled={!selectedRows.length || isSendingBatch}
                onClick={handleBatchEmail}
                className="bg-[#0d6efd] hover:bg-[#0b5ed7] disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded text-xs transition-colors"
              >
                Email
              </button>
            </div>
          </div>
          <TableTestimonyReviewed className="px-3">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Select all testimonies in this filter"
                    checked={allFilteredSelected}
                    onChange={toggleAllFiltered}
                    disabled={filteredIds.length === 0}
                  />
                </th>
                <th>Name</th>
                <th>Category</th>
                <th>Testimony</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {reviewedTotal === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-[#178B96]">
                    {categoryFilter === "all"
                      ? "No reviewed testimonies yet."
                      : "No reviewed testimonies in this category."}
                  </td>
                </tr>
              ) : (
                reviewedRows.map((row) => (
                  <tr key={row.id}>
                    <td className="text-center align-top">
                      <input
                        type="checkbox"
                        aria-label="Select testimony"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleRowSelected(row.id)}
                      />
                    </td>
                    <td className="text-left align-top">
                      {[row.name, row.last_name].filter(Boolean).join(" ") ||
                        "—"}
                    </td>
                    <td className="text-left align-top">{row.category || "—"}</td>
                    <td className="text-left align-top max-w-[280px] whitespace-pre-wrap break-words">
                      {row.testimony || "—"}
                    </td>
                    <td className="text-left align-top whitespace-nowrap">
                      {row.twilio_number || "—"}
                    </td>
                    <td className="text-left align-top whitespace-nowrap text-sm">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="text-center align-top">
                      <div className="flex flex-wrap justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleShareTestimonySlack(row)}
                          className="bg-[#4A154B] hover:bg-[#611f69] text-white px-2 py-1 rounded text-xs transition-colors whitespace-nowrap"
                        >
                          Slack
                        </button>
                        <button
                          type="button"
                          onClick={() => shareTestimonyByEmail(row)}
                          className="bg-[#0d6efd] hover:bg-[#0b5ed7] text-white px-2 py-1 rounded text-xs transition-colors whitespace-nowrap"
                        >
                          Email
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </TableTestimonyReviewed>
        </div>
      )}

      {activeTab === "new" && (
        <PastoralPagination
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          total_count={totalCount}
          handlePageChange={handlePageChange}
        />
      )}

      {activeTab === "reviewed" && (
        <PastoralPagination
          currentPage={safeReviewedPage}
          itemsPerPage={itemsPerPage}
          total_count={reviewedTotal}
          handlePageChange={handleReviewedPageChange}
        />
      )}

      <TestimonyDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleDialogSuccess}
        callDetail={selectedCallDetail}
        isReviewMode={true}
      />
    </div>
  );
};

const tableTestimonyShared = `
  width: 100%;
  border: 1px solid #00323b;
  thead {
    font-family: "Futura PT", sans-serif;
    font-size: 16px;
    font-weight: 700;
    flex-shrink: 0;
    border-bottom: 1px solid #00323b;
    background-color: #f1f1f1;
    color: #178b96;
    text-align: center;
    height: 45px;
    letter-spacing: 0.1em;
  }
  tbody {
    text-align: center;
    tr {
    }
    td {
      padding: 0.5em;
      border-bottom: 1px solid #00323b;
      background-color: #fff;
      color: #178b96;
      font-family: "Futura PT", sans-serif;
      font-size: 16px;
      font-weight: 400;
      letter-spacing: 0.1em;
    }
  }
`;

const TableTestimony = styled.table`
  ${tableTestimonyShared}
`;

const TableTestimonyReviewed = styled.table`
  ${tableTestimonyShared}
  thead th {
    font-size: 14px;
    padding: 0.35em 0.5em;
  }
  thead th:first-child,
  tbody td:first-child {
    width: 44px;
    min-width: 44px;
    text-align: center;
  }
  tbody td {
    font-size: 14px;
    vertical-align: top;
  }
  input[type="checkbox"] {
    appearance: auto;
    -webkit-appearance: checkbox;
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    margin: 0;
    cursor: pointer;
    accent-color: #178b96;
    opacity: 1;
    visibility: visible;
    display: inline-block;
    vertical-align: middle;
  }
`;
